import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';

import { PLATFORM_NAV, PLATFORM_SETTINGS_SECTIONS } from '../nav';
import { SettingsMenu } from '../SettingsMenu';
import { authApi, endSession } from '@/features/auth';
import { useSession } from '@/app/store/session.store';
import { Avatar } from '@/shared/ui/atoms/Avatar';
import { Icon } from '@/shared/ui/atoms/Icon';
import '../Sidebar/Sidebar.styl';

/**
 * Administrative menu for platform admins: tenants, users, invitations, settings. Same glass sidebar as the
 * organisations' app (colour-coded sections, no icon rail).
 */
export function PlatformSidebar() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useSession((s) => s.user);
  const logout = useMutation({
    mutationFn: authApi.logout,
    onSettled: () => {
      endSession();
      navigate('/login', { replace: true });
    }
  });
  return (
    <div className="w-sidebar">
      <div className="w-sidebar__workspace">
        <div className="w-sidebar__tenant">
          <Avatar
            initials="SP"
            color="#e0f7f9"
            src={user?.tenantLogoUrl ?? null}
            size={36}
            square
            className="w-sidebar__tenant-mark"
          />
          <div className="w-sidebar__tenant-text">
            <div className="w-sidebar__tenant-name">
              {t('shell.platformName')}
            </div>
            <div className="w-sidebar__tenant-meta">
              {t('shell.platformMeta')}
            </div>
          </div>
        </div>
      </div>
      <div className="w-sidebar__scroll">
        <section className="w-sidebar__section w-sidebar__section--menu">
          <div className="w-sidebar__label">{t('shell.manage')}</div>
          <nav className="w-sidebar__nav" aria-label={t('nav.platform')}>
            {PLATFORM_NAV.filter((n) => n.key !== 'settings').map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  isActive
                    ? 'w-sidebar__item w-sidebar__item--active'
                    : 'w-sidebar__item'
                }
              >
                <Icon name={n.icon} />
                <span className="w-sidebar__item-label">{t(`nav.${n.key}`)}</span>
              </NavLink>
            ))}
          </nav>
        </section>

        <section className="w-sidebar__section w-sidebar__section--general">
          <div className="w-sidebar__label">{t('shell.general')}</div>
          <nav className="w-sidebar__nav" aria-label={t('nav.general')}>
            <SettingsMenu
              to="/platform/settings"
              label={t('nav.settings')}
              sections={PLATFORM_SETTINGS_SECTIONS}
            />
            <button
              type="button"
              className="w-sidebar__item"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
            >
              <Icon name="logout" />
              <span className="w-sidebar__item-label">{t('shell.logout')}</span>
            </button>
          </nav>
        </section>
      </div>
    </div>
  );
}
